import { useEffect, useRef, useState } from 'preact/hooks';
import { Link } from 'wouter-preact';
import { api } from '@/lib/teaching/teacher-api';
import { useAssessmentId, teachHref, invalidateDataset } from '@/lib/teaching/state';
import { errText, waitForJob, CONF_STYLE, type Job } from '@/lib/teaching/slice2';
import { verification, type JudgementsView, type JudgementRowView } from '@/lib/teaching/slice3';
import { Card } from '@/components/teaching/ui';
import { Btn, ErrorLine, inputCls, val } from '@/components/teaching/form';
import { Pill, SetupPage, Note, Tabs, selectCls, NoAiNote } from '@/components/teaching/setup';
import { useAiReady, aiBlockText } from '@/lib/teaching/ai-ready';
import { MypTabs } from './Descriptors';

// MYP Judgements (§04, SOP §28-§29). The AI recommends a level from approved
// evidence; the teacher confirms or sets it, one student at a time (no bulk
// accept: the teacher is the final assessor). No percentage is shown here.

type Ctx = { a: string; reload: () => Promise<void> };

export function Judgements() {
  const a = useAssessmentId();
  const [v, setV] = useState<JudgementsView | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [cls, setCls] = useState('all');
  const [job, setJob] = useState<Job | null>(null);

  const reload = async () => { if (a) setV(await api<JudgementsView>('GET', `/api/t/assessments/${a}/judgements`)); };
  useEffect(() => { setV(null); setErr(null); void reload().catch((e) => setErr(errText(e))); }, [a]);
  const changed = async () => { if (a) invalidateDataset(a); await reload(); };

  const classes = v ? Array.from(new Set(v.rows.map((r) => r.class_name))).map((c) => ({ id: c, label: c })) : [];
  const rows = v?.rows.filter((r) => cls === 'all' || r.class_name === cls) ?? [];
  const toJudge = rows.filter((r) => r.ready && r.judgement?.status !== 'confirmed' && !(r.proposal?.status === 'pending' && !r.proposal.stale));
  const running = job && (job.status === 'queued' || job.status === 'running');
  const ai = useAiReady();

  async function run() {
    if (!a || !toJudge.length) return;
    setErr(null);
    try {
      const r = await api<{ job: Job }>('POST', `/api/t/assessments/${a}/judgements/jobs`, { studentIds: toJudge.map((x) => x.student_id) });
      setJob(r.job);
      const done = await waitForJob(r.job.id, setJob);
      if (done.status === 'failed') setErr(`AI judgement failed: ${done.error ?? 'unknown error'}`);
      await reload();
    } catch (e) { setErr(errText(e)); }
  }

  const ctx: Ctx | null = a ? { a, reload: changed } : null;
  // Columns with nothing in them stay out of the way.
  const showId = rows.some((r) => r.student_code);
  const showAi = ai !== false || rows.some((r) => r.proposal);
  const showStrands = rows.some((r) => (r.proposal?.strand_evidence ?? []).length > 0);
  const counts = v ? { confirmed: v.rows.filter((r) => r.judgement?.status === 'confirmed').length, review: v.rows.filter((r) => r.judgement?.status === 'needs_review').length, present: v.rows.filter((r) => r.attendance === 'present').length } : null;
  return (
    <SetupPage a={a} title="MYP levels" actions={a && <MypTabs a={a} />}>
      <ErrorLine error={err} />
      {!v && !err && <div class="text-[13px] text-[var(--color-text-muted)]">Loading…</div>}
      {v && ctx && (
        <>
          {v.descriptor_problem && <Note tone="warn">{v.descriptor_problem}. <Link href={teachHref('/setup/descriptors', { a: ctx.a })} class="underline">Descriptors</Link></Note>}
          <Card title={`Criterion ${v.assessment.criterion} · Year ${v.assessment.year_level}`} sub="The AI suggests a level from approved marks and the MYP descriptors (best fit, never a percentage). You can also set any level yourself. You confirm each one.">
            <div class="flex flex-wrap items-center gap-2">
              <Btn kind="primary" disabled={!!running || !toJudge.length || !!v.descriptor_problem || ai !== true} title={ai === false ? aiBlockText() : undefined} onClick={run}>
                {running ? 'AI is judging…' : `Recommend levels (${toJudge.length})`}
              </Btn>
              {ai === false && <NoAiNote />}
              {counts && <span class="text-[12.5px] text-[var(--color-text-muted)]">{counts.confirmed} of {counts.present} confirmed{counts.review ? ` · ${counts.review} need re-checking` : ''}</span>}
            </div>
            {job?.status === 'done' && <div class="mt-2"><Note tone="ok">AI levels ready. Check each row and confirm.</Note></div>}
          </Card>
          {classes.length > 1 && <Tabs label="Class" value={cls} onChange={setCls} options={[{ id: 'all', label: 'All' }, ...classes]} />}

          <div class="lg:hidden flex flex-col gap-2">
            {rows.map((r) => (
              <div key={r.student_id} data-student={r.name} class="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-3 flex flex-col gap-2 min-w-0">
                <div class="flex items-center gap-2 min-w-0">
                  <b class="text-[14px] text-[var(--color-text)] truncate">{r.name}</b>
                  <span class="text-[11.5px] text-[var(--color-text-faint)]">{r.class_name}{r.student_code ? ` · ${r.student_code}` : ''}</span>
                  <span class="ml-auto"><Pill {...verification(r)} /></span>
                </div>
                {!r.ready && <div class="text-[12px] text-[#b45309]">Not ready yet: {r.reasons.join('; ')}</div>}
                {(r.proposal?.strand_evidence ?? []).length > 0 && <StrandEvidence r={r} />}
                {(r.proposal || (showAi && r.attendance === 'present' && r.judgement?.status !== 'confirmed')) && <AiLevel r={r} />}
                <TeacherLevel ctx={ctx} r={r} />
              </div>
            ))}
          </div>

          <div class="hidden lg:block"><Card class="!p-0 overflow-hidden">
            <div class="overflow-x-auto">
              <table class="w-full text-[12.5px] border-collapse">
                <thead>
                  <tr class="text-left text-[11px] uppercase tracking-wider text-[var(--color-text-faint)] bg-[var(--color-elevated)]">
                    <th class="p-2">Student</th>{showId && <th class="p-2">Student ID</th>}<th class="p-2">Status</th>{showStrands && <th class="p-2">Strand evidence</th>}{showAi && <th class="p-2 w-[28%]">AI suggestion</th>}
                    <th class="p-2 w-[30%]">Your level and notes</th><th class="p-2">Verification</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.student_id} data-student={r.name} class="border-t border-[var(--color-border)] align-top">
                      <td class="p-2 text-[var(--color-text)]"><b>{r.name}</b><div class="text-[11px] text-[var(--color-text-faint)]">{r.class_name}</div></td>
                      {showId && <td class="p-2 text-[12px] text-[var(--color-text-muted)]">{r.student_code ?? '-'}</td>}
                      <td class="p-2 text-[12px]">{r.ready ? <span class="text-[var(--color-text-muted)]">Marks approved</span> : <span class="text-[#b45309] first-letter:uppercase inline-block">{r.reasons.join('; ')}</span>}</td>
                      {showStrands && <td class="p-2"><StrandEvidence r={r} /></td>}
                      {showAi && <td class="p-2"><AiLevel r={r} /></td>}
                      <td class="p-2"><TeacherLevel ctx={ctx} r={r} /></td>
                      <td class="p-2"><Pill {...verification(r)} /></td>
                    </tr>
                  ))}
                  {rows.length === 0 && <tr><td colSpan={7} class="p-4 text-[13px] text-[var(--color-text-muted)]">No students in this test.</td></tr>}
                </tbody>
              </table>
            </div>
          </Card></div>
        </>
      )}
    </SetupPage>
  );
}

function StrandEvidence({ r }: { r: JudgementRowView }) {
  const se = r.proposal?.strand_evidence ?? [];
  if (!se.length) return <span class="text-[12px] text-[var(--color-text-faint)]">-</span>;
  return (
    <ul class="text-[12px] text-[var(--color-text)] flex flex-col gap-0.5">
      {se.map((s) => <li key={s.strand}><b>{s.strand}</b>: band {s.band} <span class="text-[var(--color-text-faint)]">({s.evidence.join(', ') || 'no items'})</span></li>)}
    </ul>
  );
}

function AiLevel({ r }: { r: JudgementRowView }) {
  const p = r.proposal;
  if (!p) return r.judgement?.status === 'confirmed' && r.attendance === 'present' ? <span class="text-[12px] text-[var(--color-text-faint)]">-</span> : <span class="text-[12px] text-[var(--color-text-faint)]">{r.attendance === 'present' ? 'No AI suggestion yet' : '-'}</span>;
  const stale = p.status === 'stale' || !!p.stale;
  return (
    <div class={'flex flex-col gap-1 min-w-0 ' + (stale ? 'opacity-60' : '')}>
      <div class="flex flex-wrap items-center gap-1.5">
        <span class="text-[12px] text-[var(--color-text-muted)]">AI suggests</span>
        <b class="text-[18px] leading-none text-[var(--color-text)] tabular-nums" data-ai-level>{p.ai_level ?? '-'}</b>
        <Pill {...CONF_STYLE[p.confidence]} />
        {stale && <Pill label="Out of date: run again" bg="#e2e3e6" />}
      </div>
      {p.insufficient_reason && <div class="text-[12px] text-[var(--color-text)]">{p.insufficient_reason}</div>}
      {p.rationale && <div class="text-[12px] text-[var(--color-text)] break-words">{p.rationale}</div>}
      {p.limiting_evidence && <div class="text-[11.5px] text-[var(--color-text-muted)] break-words">Holding it back: {p.limiting_evidence}</div>}
      {p.review_reason && <div class="text-[11.5px] text-[#b45309] break-words">Check: {p.review_reason}</div>}
    </div>
  );
}

function TeacherLevel({ ctx, r }: { ctx: Ctx; r: JudgementRowView }) {
  const j = r.judgement;
  const [level, setLevel] = useState<string>(j?.teacher_level != null ? String(j.teacher_level) : '');
  const [note, setNote] = useState(j?.teacher_note ?? '');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [naOpen, setNaOpen] = useState(false);
  const [naReason, setNaReason] = useState('');
  const rev = j?.judgement_revision ?? 0;
  const last = useRef(rev);
  useEffect(() => {
    if (last.current === rev) return; // not on mount: it would run after paint and undo a first edit
    last.current = rev;
    setLevel(j?.teacher_level != null ? String(j.teacher_level) : ''); setNote(j?.teacher_note ?? '');
  }, [rev]);

  async function send(body: Record<string, unknown>) {
    setErr(null); setBusy(true);
    try {
      await api('PUT', `/api/t/assessments/${ctx.a}/judgements/${r.student_id}`, { expectedEvidenceRevision: r.evidence_revision, ...body }, j?.judgement_revision ?? 0);
      await ctx.reload();
    } catch (e) { setErr(errText(e)); await ctx.reload(); }
    setBusy(false);
  }
  if (r.attendance !== 'present') return <span class="text-[12px] text-[var(--color-text-faint)]">No level: {r.attendance === 'catchup' ? 'catch-up pending' : r.attendance}</span>;
  const p = r.proposal;
  const canAccept = p && !r.accept_block;
  const unchanged = j?.status === 'confirmed' && level === String(j.teacher_level) && note === (j.teacher_note ?? '');
  return (
    <div class="flex flex-col gap-1.5 min-w-0">
      {j?.status === 'needs_review' && j.teacher_level !== null && (
        <div class="text-[12px] text-[var(--color-text-muted)]">Was <s data-old-level class="text-[var(--color-text)]">{j.teacher_level}</s>; something it was based on changed, confirm again.</div>
      )}
      {j?.disposition === 'not_assessable' && j.status !== 'draft' && (
        <div class="text-[12px] text-[var(--color-text)]" data-not-assessable><b>Not assessable</b>: {j.disposition_reason}{j.status === 'needs_review' ? ' (evidence changed, record again or set a level)' : ''}</div>
      )}
      {j?.status === 'confirmed' && j.disposition !== 'not_assessable' && <div class="text-[12px] text-[var(--color-text)]">Confirmed <b class="text-[16px] tabular-nums">{j.teacher_level}</b>{j.source === 'ai' ? ' (AI level accepted)' : ''}</div>}
      {p && p.ai_level !== null && j?.status !== 'confirmed' && (
        canAccept
          ? <div><Btn disabled={busy} onClick={() => send({ action: 'accept', proposalId: p.id, note })}>Accept AI level {p.ai_level}</Btn></div>
          : <div class="text-[11.5px] text-[var(--color-text-muted)]">{p.status === 'accepted' ? 'This AI level was made on the old evidence: run "Recommend levels" again for a fresh one, or set the level.' : `AI level cannot be accepted as-is: ${r.accept_block}`}</div>
      )}
      <div class="flex flex-wrap items-center gap-1.5">
        <select class={selectCls + ' w-20'} aria-label={`Level for ${r.name}`} value={level} onChange={(e) => setLevel(val(e))} disabled={!r.ready}>
          <option value="">Level</option>
          {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((l) => <option key={l} value={String(l)}>{l}</option>)}
        </select>
        <Btn kind="primary" disabled={busy || !r.ready || level === '' || unchanged} onClick={() => send({ action: 'confirm', teacher_level: Number(level), note })}>{j?.status === 'confirmed' ? 'Update' : 'Confirm'}</Btn>
        {!naOpen && r.ready && j?.disposition !== 'not_assessable' && (
          <button type="button" class="h-9 px-2 rounded-lg hover:bg-[var(--color-elevated)] disabled:opacity-50" style={{ color: 'var(--color-text-faint)' }} aria-label={`More options for ${r.name}`} title="More: mark as not assessable" disabled={busy} onClick={() => setNaOpen(true)}>&#8943;</button>
        )}
      </div>
      <input class={inputCls + ' w-full'} aria-label={`Note for ${r.name}`} placeholder="Note (optional)" value={note} onInput={(e) => setNote(val(e))} />
      {!naOpen ? (
        null
      ) : (
        <div class="flex flex-wrap items-center gap-1.5">
          <input class={inputCls + ' flex-1 min-w-[140px]'} aria-label={`Why ${r.name} is not assessable`} placeholder="Why can this student not be given a level? (required)" value={naReason} onInput={(e) => setNaReason(val(e))} />
          <Btn disabled={busy || !naReason.trim()} onClick={async () => { await send({ action: 'not_assessable', reason: naReason }); setNaOpen(false); setNaReason(''); }}>Record not assessable</Btn>
          <Btn onClick={() => setNaOpen(false)}>Cancel</Btn>
        </div>
      )}
      <ErrorLine error={err} />
    </div>
  );
}
